import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  FolderIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { tasksAPI, teamsAPI, projectsAPI } from '../services/api';
import UserTaskModal from '../components/user/UserTaskModal';
import DayTasksModal from '../components/calendar/DayTasksModal';
import toast from 'react-hot-toast';

interface CalendarTask {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  dueDate: string;
  team?: any;
  project?: any;
}

interface MonthData {
  year: number;
  month: number;
  tasksByDate: Record<string, CalendarTask[]>;
  totalTasks: number;
}

const UserCalendar: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthData, setMonthData] = useState<MonthData | null>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  const [teams, setTeams] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  // Day tasks modal state
  const [isDayTasksModalOpen, setIsDayTasksModalOpen] = useState(false);
  const [selectedDayTasks, setSelectedDayTasks] = useState<CalendarTask[]>([]);
  const [selectedDayDate, setSelectedDayDate] = useState<string>('');

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  useEffect(() => {
    if (user) {
      loadInitialData();
      loadMonthData();
    }
  }, [user, currentYear, currentMonth]);

  const loadInitialData = async () => {
    try {
      const [teamsData, projectsData] = await Promise.all([
        teamsAPI.getAll(),
        projectsAPI.getAll({ isArchived: false }),
      ]);
      setTeams(teamsData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadMonthData = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      // Get user's tasks for the current month
      const data = await tasksAPI.getMonthTasks(currentYear, currentMonth, user.id);
      setMonthData(data);
    } catch (error) {
      console.error('Error loading calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  }, [user, currentYear, currentMonth]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleTaskClick = async (task: any) => {
    try {
      if (task.type === 'recurring' || task.isRecurringInstance || task.id.startsWith('recurring-')) {
        // For recurring tasks, show a message or handle differently
        toast(`This is a recurring task. You can view it but cannot modify individual instances.`, {
          icon: 'ℹ️',
          duration: 4000,
        });

        // Still try to get the task details for viewing
        const fullTask = await tasksAPI.getById(task.id);
        setSelectedTask(fullTask);
        setIsTaskModalOpen(true);
        return;
      }

      // Get full task details for regular tasks
      const fullTask = await tasksAPI.getById(task.id);
      setSelectedTask(fullTask);
      setIsTaskModalOpen(true);
    } catch (error) {
      console.error('Error loading task details:', error);
      toast.error('Failed to load task details');
    }
  };

  const handleTaskUpdate = (updatedTask: any) => {
    loadMonthData();
  };

  const handleDateClick = (dateStr: string) => {
    const tasksForDay = monthData?.tasksByDate[dateStr] || [];
    setSelectedDayDate(dateStr);
    setSelectedDayTasks(tasksForDay);
    setIsDayTasksModalOpen(true);
  };





  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-500';
      case 'HIGH':
        return 'bg-yellow-500';
      case 'NORMAL':
        return 'bg-blue-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="min-h-32 p-2 border border-gray-200 bg-gray-50" />
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const tasksForDay = monthData?.tasksByDate[dateStr] || [];
      const isToday = new Date().toDateString() === new Date(currentYear, currentMonth - 1, day).toDateString();

      days.push(
        <motion.div
          key={day}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: day * 0.01 }}
          className={`min-h-32 p-2 border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer relative group ${
            isToday ? 'ring-2 ring-primary-500 bg-primary-50' : ''
          }`}
          onClick={() => handleDateClick(dateStr)}
        >
          <div className={`flex items-center justify-between mb-2`}>
            <div className={`text-sm font-medium ${isToday ? 'text-primary-600' : 'text-gray-900'}`}>
              {day}
            </div>
          </div>
          <div className="space-y-1">
            {tasksForDay.slice(0, 3).map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className="relative"
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTaskClick(task);
                  }}
                  className={`text-xs p-1 rounded border cursor-pointer hover:shadow-sm transition-all ${getStatusColor(task.status)}`}
                >
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                    <span className="truncate flex-1">{task.title}</span>
                    {isOverdue(task.dueDate) && task.status !== 'DONE' && (
                      <ExclamationTriangleIcon className="w-3 h-3 text-red-500" title="Overdue" />
                    )}
                  </div>
                  {task.team && (
                    <div className="flex items-center space-x-1 mt-1">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: task.team.color }}
                      />
                      <span className="text-xs text-gray-600 truncate">{task.team.name}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            {tasksForDay.length > 3 && (
              <div className="text-xs text-gray-500 text-center py-1">
                +{tasksForDay.length - 3} more
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <CalendarIcon className="w-8 h-8 text-primary-600" />
              <span>My Calendar</span>
            </h1>
            <p className="text-gray-600">
              View your assigned tasks by date • Click tasks to view details ({monthData?.totalTasks || 0} tasks this month)
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Today
            </button>

          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <h2 className="text-xl font-semibold text-gray-900">
            {monthNames[currentMonth - 1]} {currentYear}
          </h2>

          <button
            onClick={() => navigateMonth('next')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50">
            {dayNames.map((day) => (
              <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {renderCalendarDays()}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <span>Urgent Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <span>High Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <span>Normal Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span>Low Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-3 h-3 text-red-600" />
              <span>Overdue Task</span>
            </div>
          </div>
        </div>

        {/* Task Detail Modal */}
        <UserTaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdate={handleTaskUpdate}
        />

        {/* Day Tasks Modal */}
        <DayTasksModal
          isOpen={isDayTasksModalOpen}
          onClose={() => setIsDayTasksModalOpen(false)}
          date={selectedDayDate}
          tasks={selectedDayTasks}
          onTaskClick={(task) => {
            handleTaskClick(task);
            setIsDayTasksModalOpen(false);
          }}
        />

      </div>
    </Layout>
  );
};

export default UserCalendar;
